import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { X, LayoutDashboard, Bot, ScanLine, FileText, Banknote, Hammer, Search, ShoppingBag, Gift, FileEdit, LayoutTemplate, Users, PiggyBank, Video, Trophy, Wallet, Zap, Award, Receipt, Settings, HelpCircle, Sparkles, Shield, LogOut, Target, Plug, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

// Official Zuntria global navigation — exact section order, do not rename/rearrange
const NAV_SECTIONS = [
  {
    heading: "Success Engine",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "/assistants", label: "AI Assistants", icon: Bot },
      { to: "/analyze", label: "AI Scanner", icon: ScanLine },
      { to: "/documents", label: "My Documents", icon: FileText },
    ],
  },
  {
    heading: "Growth Layer",
    items: [
      { to: "/money", label: "Money Engine", icon: Banknote },
      { to: "/autopilot", label: "Goal Autopilot", icon: Target },
      { to: "/hustles", label: "Hustle Assist", icon: Hammer },
      { to: "/truffl", label: "Truffl Client Finder", icon: Search },
      { to: "/marketplace", label: "Marketplace", icon: ShoppingBag },
      { to: "/referrals", label: "Referrals", icon: Gift },
    ],
  },
  {
    heading: "Execution Layer",
    items: [
      { to: "/builder/cv", label: "CV Builder", icon: FileEdit },
      { to: "/templates", label: "Templates", icon: LayoutTemplate },
      { to: "/groups", label: "Ajo Groups", icon: Users },
      { to: "/kolo", label: "Kolo Savings", icon: PiggyBank },
      { to: "/studio", label: "Creator Studio", icon: Video },
      { to: "/challenges", label: "Challenges", icon: Trophy },
    ],
  },
  {
    heading: "System Layer",
    items: [
      { to: "/wallet", label: "Wallet", icon: Wallet },
      { to: "/credits", label: "AI Credits", icon: Zap },
      { to: "/rewards", label: "Rewards", icon: Award },
      { to: "/history", label: "History", icon: Receipt },
      { to: "/applications", label: "Job Tracker", icon: ClipboardList },
      { to: "/partners", label: "Partner Offers", icon: Gift },
      { to: "/integrations", label: "Integrations", icon: Plug },
      { to: "/settings", label: "Settings", icon: Settings },
      { to: "/pricing", label: "Pricing", icon: Sparkles },
      { to: "/help", label: "Help", icon: HelpCircle },
    ],
  },
];

export function SidebarNav({ open, onClose }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  if (!open) return null;
  const isAdmin = user?.is_admin === true;

  return createPortal(
    <div className="fixed inset-0 z-[95]" data-testid="sidebar-nav">
      <div className="absolute inset-0 bg-background/70 backdrop-blur-sm" onClick={onClose} data-testid="sidebar-overlay" />
      <aside className="absolute right-0 top-0 flex h-full w-80 max-w-[85vw] flex-col overflow-y-auto border-l border-border bg-card p-5 shadow-2xl">
        <div className="flex items-center justify-between">
          <span className="font-display text-lg text-primary">Zuntria</span>
          <button onClick={onClose} className="rounded-md p-1.5 text-muted-foreground hover:bg-secondary" data-testid="sidebar-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {NAV_SECTIONS.map((s, i) => (
          <div key={s.heading} className={`${i === 0 ? "mt-5" : "mt-6 border-t border-border/60 pt-5"}`} data-testid={`sidebar-section-${s.heading.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className="px-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-primary">{s.heading}</p>
            <div className="mt-2 space-y-0.5">
              {s.items.map((it) => (
                <Link key={it.to + it.label} to={it.to} onClick={onClose}
                  className="flex items-center gap-3 rounded-lg px-2 py-2 text-sm text-foreground/90 transition-colors hover:bg-secondary"
                  data-testid={`sidebar-link-${it.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <it.icon className="h-4 w-4 text-muted-foreground" /> {it.label}
                </Link>
              ))}
            </div>
          </div>
        ))}

        <div className="mt-6 border-t border-border/60 pt-5">
          {isAdmin && (
            <Link to="/admin" onClick={onClose} className="flex items-center gap-3 rounded-lg px-2 py-2 text-sm hover:bg-secondary" data-testid="sidebar-link-admin">
              <Shield className="h-4 w-4 text-muted-foreground" /> Admin
            </Link>
          )}
          {user ? (
            <Button variant="outline" className="mt-2 w-full" onClick={async () => { await logout(); onClose(); nav("/"); }} data-testid="sidebar-logout">
              <LogOut className="mr-2 h-4 w-4" /> Logout
            </Button>
          ) : (
            <div className="mt-2 flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { onClose(); nav("/login"); }} data-testid="sidebar-login">Login</Button>
              <Button className="flex-1" onClick={() => { onClose(); nav("/signup"); }} data-testid="sidebar-signup">Get started</Button>
            </div>
          )}
        </div>
      </aside>
    </div>,
    document.body
  );
}
